// SurfaceVistas — one clean low-poly silhouette per inhabited chapter.
// Kept deliberately sparse: a thin plate + a few readable shapes.
// Only the active scene's vista is drawn, so the planet never wears
// four city kits at once.

import { useMemo, useRef, type ReactNode } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

type VistaKind = 'paris' | 'tokyo' | 'kuala-lumpur' | 'sydney';

interface VistaSpec {
  id: string;
  sceneId: number;
  kind: VistaKind;
  position: [number, number, number];
  scale: number;
  yaw: number;
  label: string;
  description: string;
}

/** Scene ids that own a surface vista (and therefore hide their landmark). */
export const VISTA_SCENE_IDS: ReadonlySet<number> = new Set([1, 2, 3, 4]);

const VISTAS: VistaSpec[] = [
  {
    id: 'paris-tower',
    sceneId: 1,
    kind: 'paris',
    position: [2.18, 0.52, 1.02],
    scale: 0.5,
    yaw: -0.35,
    label: 'Paris signal',
    description: 'A warm tower signal on the first inhabited edge of the world.',
  },
  {
    id: 'tokyo-fuji',
    sceneId: 2,
    kind: 'tokyo',
    position: [-1.42, 1.36, 2.08],
    scale: 0.52,
    yaw: 0.45,
    label: 'Tokyo ridge',
    description: 'A compressed skyline and a quiet mountain line.',
  },
  {
    id: 'kuala-lumpur-towers',
    sceneId: 3,
    kind: 'kuala-lumpur',
    position: [0.9, -0.9, 2.48],
    scale: 0.54,
    yaw: -0.1,
    label: 'Kuala Lumpur relay',
    description: 'Two towers carry the projects chapter back into orbit.',
  },
  {
    id: 'sydney-harbor',
    sceneId: 4,
    kind: 'sydney',
    position: [-1.86, 1.9, -0.92],
    scale: 0.5,
    yaw: 0.2,
    label: 'Sydney harbor',
    description: 'A small harbor for fragments, field notes, and unfinished ideas.',
  },
];

const LOCAL_UP = new THREE.Vector3(0, 1, 0);
const INK = '#F5EFEA';
const ACCENT = '#01847F';
const DEEP = '#122E8A';
const WARM = '#F9D2E4';

export interface SurfaceVistasProps {
  activeSceneIndex: number;
}

function useSurfaceTransform(position: [number, number, number], yaw: number) {
  return useMemo(() => {
    const outward = new THREE.Vector3(...position).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(LOCAL_UP, outward);
    const yawQuat = new THREE.Quaternion().setFromAxisAngle(LOCAL_UP, yaw);
    quat.multiply(yawQuat);
    const euler = new THREE.Euler().setFromQuaternion(quat);
    return {
      rotation: [euler.x, euler.y, euler.z] as [number, number, number],
    };
  }, [position, yaw]);
}

function VistaShell({
  spec,
  children,
}: {
  spec: VistaSpec;
  children: ReactNode;
}) {
  const { rotation } = useSurfaceTransform(spec.position, spec.yaw);
  const shellRef = useRef<THREE.Group | null>(null);

  useFrame((_, delta) => {
    const shell = shellRef.current;
    if (!shell) return;
    const damp = 1 - Math.exp(-delta * 5);
    shell.scale.setScalar(THREE.MathUtils.lerp(shell.scale.x, spec.scale, damp));
  });

  const openVista = (event: { stopPropagation: () => void }) => {
    if (typeof window === 'undefined' || window.__planetMode !== 'exploration') return;
    event.stopPropagation();
    const point = new THREE.Vector3();
    shellRef.current?.getWorldPosition(point);
    window.__planetShowInfoCard?.({
      kind: 'vista',
      id: spec.id,
      title: spec.label,
      blurb: spec.description,
      worldPos: [point.x, point.y, point.z],
    });
  };

  return (
    <group
      ref={shellRef}
      name={`surface-vista-${spec.id}`}
      position={spec.position}
      rotation={rotation}
      scale={spec.scale * 0.92}
      raycast={() => null}
      userData={{ surfaceVista: spec.id }}
    >
      {/* Single quiet plate — no rim forest, no signal bars. */}
      <mesh
        name={`${spec.id}-plate`}
        position={[0, 0.01, 0]}
        onClick={openVista}
        onPointerOver={() => {
          if (typeof window !== 'undefined' && window.__planetMode === 'exploration') {
            document.body.style.cursor = 'pointer';
          }
        }}
        onPointerOut={() => {
          document.body.style.cursor = '';
        }}
      >
        <cylinderGeometry args={[0.58, 0.64, 0.028, 6]} />
        <meshStandardMaterial
          color={DEEP}
          transparent
          opacity={0.55}
          roughness={0.88}
          metalness={0.02}
          flatShading
          depthWrite={false}
        />
      </mesh>
      {children}
    </group>
  );
}

/** About — one tapered tower, one crossbar, one tip. */
function ParisTower() {
  const flagRef = useRef<THREE.Mesh | null>(null);
  const FLAG_X = [0.09, 0.13, 0.08, 0.12] as const;

  useFrame((state) => {
    const flag = flagRef.current;
    if (!flag) return;
    const reduced =
      typeof window !== 'undefined' && window.__planetReducedMotion === true;
    if (reduced) {
      flag.scale.x = 1;
      return;
    }
    const frame = Math.floor(state.clock.elapsedTime * 8);
    flag.scale.x = FLAG_X[frame % FLAG_X.length] / 0.1;
  });

  return (
    <group name="vista-paris-tower">
      <mesh position={[0, 0.55, 0]} raycast={() => null}>
        <cylinderGeometry args={[0.028, 0.09, 1.05, 4]} />
        <meshStandardMaterial
          color={INK}
          emissive={INK}
          emissiveIntensity={0.12}
          roughness={0.72}
          flatShading
        />
      </mesh>
      <mesh position={[0, 0.78, 0]} raycast={() => null}>
        <boxGeometry args={[0.28, 0.03, 0.08]} />
        <meshStandardMaterial color={WARM} roughness={0.65} flatShading />
      </mesh>
      <mesh position={[0, 1.12, 0]} raycast={() => null}>
        <coneGeometry args={[0.04, 0.22, 4]} />
        <meshStandardMaterial
          color={ACCENT}
          emissive={ACCENT}
          emissiveIntensity={0.28}
          roughness={0.55}
          flatShading
        />
      </mesh>
      <mesh ref={flagRef} position={[0.07, 1.2, 0]} raycast={() => null}>
        <boxGeometry args={[0.1, 0.035, 0.01]} />
        <meshBasicMaterial color={WARM} />
      </mesh>
    </group>
  );
}

/** Skills — a compressed skyline, layered ridge, and a quiet river. */
function TokyoFuji() {
  const mountainRef = useRef<THREE.Group | null>(null);
  const PEAK_Y = [1, 1.04, 0.98, 1.02] as const;
  const riverCurve = useMemo(
    () =>
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(-0.42, 0.035, 0.2),
        new THREE.Vector3(-0.2, 0.04, 0.28),
        new THREE.Vector3(0.02, 0.038, 0.19),
        new THREE.Vector3(0.24, 0.04, 0.25),
        new THREE.Vector3(0.48, 0.035, 0.16),
      ]),
    [],
  );

  useFrame((state) => {
    const mountain = mountainRef.current;
    if (!mountain) return;
    const reduced =
      typeof window !== 'undefined' && window.__planetReducedMotion === true;
    if (reduced) {
      mountain.scale.y = 1;
      return;
    }
    const frame = Math.floor(state.clock.elapsedTime * 6);
    mountain.scale.y = PEAK_Y[frame % PEAK_Y.length];
  });

  return (
    <group name="vista-tokyo-fuji">
      {[
        { x: -0.22, h: 0.42, w: 0.12 },
        { x: -0.06, h: 0.58, w: 0.14 },
        { x: 0.12, h: 0.36, w: 0.11 },
      ].map((b) => (
        <mesh
          key={b.x}
          position={[b.x, b.h / 2 + 0.02, 0.06]}
          raycast={() => null}
        >
          <boxGeometry args={[b.w, b.h, 0.12]} />
          <meshStandardMaterial color={INK} roughness={0.78} flatShading />
        </mesh>
      ))}
      <group ref={mountainRef} position={[0.2, 0.08, -0.14]}>
        {[
          { x: -0.3, z: -0.04, r: 0.18, h: 0.3, color: '#25499a' },
          { x: -0.08, z: -0.08, r: 0.28, h: 0.52, color: DEEP },
          { x: 0.17, z: -0.02, r: 0.22, h: 0.4, color: '#193b86' },
          { x: 0.37, z: -0.12, r: 0.15, h: 0.27, color: '#3157a1' },
        ].map((peak) => (
          <mesh
            key={peak.x}
            position={[peak.x, peak.h / 2, peak.z]}
            raycast={() => null}
          >
            <coneGeometry args={[peak.r, peak.h, 5]} />
            <meshStandardMaterial
              color={peak.color}
              roughness={0.9}
              flatShading
            />
          </mesh>
        ))}
        <mesh position={[-0.08, 0.48, -0.08]} raycast={() => null}>
          <coneGeometry args={[0.09, 0.12, 5]} />
          <meshStandardMaterial color={INK} roughness={0.7} flatShading />
        </mesh>
      </group>
      <mesh raycast={() => null}>
        <tubeGeometry args={[riverCurve, 18, 0.018, 5, false]} />
        <meshStandardMaterial
          color="#8ec5ff"
          emissive="#2f7fc0"
          emissiveIntensity={0.2}
          roughness={0.35}
        />
      </mesh>
    </group>
  );
}

/** Projects — twin towers + one skybridge. Nothing else. */
function KualaLumpurTowers() {
  return (
    <group name="vista-kuala-lumpur-towers">
      {[-0.16, 0.16].map((x) => (
        <group key={x} position={[x, 0, 0]}>
          <mesh position={[0, 0.48, 0]} raycast={() => null}>
            <cylinderGeometry args={[0.07, 0.09, 0.92, 6]} />
            <meshStandardMaterial color={DEEP} roughness={0.78} flatShading />
          </mesh>
          <mesh position={[0, 0.98, 0]} raycast={() => null}>
            <coneGeometry args={[0.065, 0.2, 6]} />
            <meshStandardMaterial
              color={INK}
              emissive={INK}
              emissiveIntensity={0.18}
              roughness={0.6}
              flatShading
            />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 0.62, 0]} raycast={() => null}>
        <boxGeometry args={[0.32, 0.04, 0.06]} />
        <meshStandardMaterial color={ACCENT} roughness={0.55} flatShading />
      </mesh>
    </group>
  );
}

/** Notes — three sails on a quiet quay. */
function SydneyHarbor() {
  return (
    <group name="vista-sydney-harbor">
      <mesh position={[0, 0.05, 0]} raycast={() => null}>
        <boxGeometry args={[0.72, 0.06, 0.2]} />
        <meshStandardMaterial color={DEEP} roughness={0.85} flatShading />
      </mesh>
      {[
        { x: -0.2, rot: -0.55, s: 0.95 },
        { x: 0.0, rot: -0.42, s: 1.1 },
        { x: 0.2, rot: -0.3, s: 0.88 },
      ].map((sail) => (
        <mesh
          key={sail.x}
          position={[sail.x, 0.28, 0.02]}
          rotation={[0, 0, sail.rot]}
          scale={[0.7, sail.s, 0.28]}
          raycast={() => null}
        >
          <coneGeometry args={[0.14, 0.48, 3]} />
          <meshStandardMaterial
            color={WARM}
            emissive={WARM}
            emissiveIntensity={0.16}
            roughness={0.62}
            flatShading
          />
        </mesh>
      ))}
      <mesh position={[0, 0.08, 0.22]} rotation={[Math.PI / 2, 0, 0]} raycast={() => null}>
        <torusGeometry args={[0.36, 0.01, 4, 48, Math.PI]} />
        <meshBasicMaterial color={ACCENT} transparent opacity={0.45} />
      </mesh>
    </group>
  );
}

function renderVista(kind: VistaKind) {
  switch (kind) {
    case 'paris':
      return <ParisTower />;
    case 'tokyo':
      return <TokyoFuji />;
    case 'kuala-lumpur':
      return <KualaLumpurTowers />;
    case 'sydney':
      return <SydneyHarbor />;
    default:
      return null;
  }
}

export function SurfaceVistas({ activeSceneIndex }: SurfaceVistasProps) {
  const active = VISTAS.find((v) => v.sceneId === activeSceneIndex);
  if (!active) return null;

  return (
    <group name="surface-vistas" userData={{ pickable: false }}>
      <VistaShell key={active.id} spec={active}>
        {renderVista(active.kind)}
      </VistaShell>
    </group>
  );
}

export default SurfaceVistas;
